import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, useAuth, useLang, formatApiError, setAuthToken } from "../lib/providers";
import { FileText, Translate, Check, X } from "@phosphor-icons/react";

const ROLE_LABELS = {
  de: { admin: "Admin", expert: "Experte", user: "Anwender", handworker: "Handwerker" },
  en: { admin: "Admin", expert: "Expert", user: "User", handworker: "Handworker" },
};

export default function InviteAcceptPage() {
  const { token } = useParams();
  const { refresh } = useAuth();
  const { t, lang, toggleLang } = useLang();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/auth/invite/${token}`);
        setInvite(data);
        setName(data.name || "");
      } catch (err) {
        setError(formatApiError(err.response?.data?.detail) || err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post(`/auth/invite/${token}/accept`, { password, name });
      if (data?.access_token) setAuthToken(data.access_token);
      await refresh();
      navigate("/quotes", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] flex items-center justify-center p-8">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-[var(--brand)] flex items-center justify-center">
              <FileText weight="fill" size={14} color="white" />
            </div>
            <span className="font-heading font-semibold">FalkeDeck</span>
          </div>
          <button onClick={toggleLang} data-testid="invite-lang-switch"
            className="flex items-center gap-1.5 text-xs text-[var(--muted)] hover:text-[var(--ink)] transition-colors">
            <Translate size={14} />
            <span className="label-overline">{lang.toUpperCase()}</span>
          </button>
        </div>

        <div className="label-overline mb-2">{lang === "de" ? "Einladung annehmen" : "Accept invite"}</div>

        {loading ? (
          <div className="text-[var(--muted)] text-sm">…</div>
        ) : !invite ? (
          <div data-testid="invite-error">
            <h2 className="font-heading text-2xl font-semibold tracking-tight flex items-center gap-2">
              <X size={24} className="text-[#FF3B30]" />
              {lang === "de" ? "Einladung ungültig" : "Invite invalid"}
            </h2>
            <p className="text-sm text-[var(--muted)] mt-3">
              {error || (lang === "de" ? "Diese Einladung ist abgelaufen oder bereits eingelöst." : "This invite has expired or was already used.")}
            </p>
            <Link to="/login" className="inline-block mt-6 text-[var(--brand)] hover:underline text-sm">
              {lang === "de" ? "Zur Anmeldung" : "Back to login"}
            </Link>
          </div>
        ) : (
          <>
            <h2 className="font-heading text-3xl font-semibold tracking-tight">
              {lang === "de" ? "Willkommen!" : "Welcome!"}
            </h2>
            <p className="text-[var(--muted)] mt-2 text-sm">
              {lang === "de"
                ? <>Du wurdest{invite.company ? <> zu <b>{invite.company}</b></> : ""} als <b>{ROLE_LABELS[lang][invite.role]}</b> eingeladen. Setze dein Passwort, um loszulegen.</>
                : <>You have been invited{invite.company ? <> to <b>{invite.company}</b></> : ""} as <b>{ROLE_LABELS[lang][invite.role]}</b>. Set your password to get started.</>}
            </p>

            <form onSubmit={onSubmit} className="mt-8 space-y-5">
              <div>
                <label className="label-overline block mb-1.5">{t("email")}</label>
                <input
                  data-testid="invite-email-display"
                  value={invite.email}
                  readOnly
                  className="w-full h-11 px-3 bg-[var(--row-hover)] border border-[var(--border)] rounded-sm text-sm text-[var(--muted)]"
                />
              </div>
              <div>
                <label className="label-overline block mb-1.5">{t("name")}</label>
                <input
                  data-testid="invite-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full h-11 px-3 bg-white border border-[var(--border)] rounded-sm text-sm focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand)]/20 focus:outline-none"
                />
              </div>
              <div>
                <label className="label-overline block mb-1.5">{t("password")}</label>
                <input
                  data-testid="invite-password"
                  type="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-11 px-3 bg-white border border-[var(--border)] rounded-sm text-sm focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand)]/20 focus:outline-none"
                />
              </div>
              {error && (
                <div data-testid="invite-error-msg" className="text-sm text-[#FF3B30] border border-[#FF3B30]/30 bg-[#FF3B30]/5 px-3 py-2 rounded-sm">
                  {error}
                </div>
              )}
              <button
                data-testid="invite-submit"
                type="submit"
                disabled={submitting}
                className="w-full h-11 bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-white font-medium text-sm rounded-sm transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
              >
                <Check size={16} /> {submitting ? "…" : (lang === "de" ? "Konto aktivieren" : "Activate account")}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
